/**
 * shoppable-reels.js — Aneka Studio
 *
 * Section carousel  → thumbnail strip with IntersectionObserver autoplay
 * Viewer modal      → click any reel → full-screen viewer with:
 *                       • active reel (center, playing)
 *                       • neighbouring reels (sides, dimmed)
 *                       • product panel inside the reel (compact → expanded)
 *
 * Vanilla JS only. No jQuery. No external deps.
 */

/* ============================================================
   0. ANALYTICS HELPER
   Fires a CustomEvent on document for any listener (GTM, GA4,
   Segment, Klaviyo etc.) AND calls gtag / dataLayer if present.

   Every event carries:
     event       – snake_case name
     section     – "shoppable_reels"
     reel_index  – 0-based position
     product_handle, product_title, product_url (when applicable)

   To listen in plain JS:
     document.addEventListener('sr:reel_opened', e => console.log(e.detail));

   GA4 via gtag (auto if window.gtag exists):
     All events are prefixed → "sr_reel_opened", "sr_add_to_cart", etc.

   GTM dataLayer (auto if window.dataLayer exists):
     Push {event: 'sr_reel_opened', ...detail} into dataLayer.
   ============================================================ */

const SRAnalytics = {
  /* ── Parse money string → float, e.g. "₹1,399.00" → 1399.00 ── */
  _parsePrice(str) {
    if (!str) return 0;
    return parseFloat(String(str).replace(/[^0-9.]/g, '')) || 0;
  },

  /* ── Fire Meta Pixel (fbq) if the pixel is loaded ── */
  _fbq(type, eventName, params) {
    if (typeof window.fbq !== 'function') return;
    window.fbq(type, eventName, params);
  },

  /* ── Standard event payload shape for Meta ── */
  _metaContent(reel) {
    return {
      content_ids:   [reel?.productHandle || ''],
      content_name:  reel?.productTitle   || '',
      content_type:  'product',
      currency:      'INR',
    };
  },

  /* ────────────────────────────────────────────────────────────
     Meta Pixel events
     Standard events  → fbq('track', ...)
     Custom events    → fbq('trackCustom', ...)
  ──────────────────────────────────────────────────────────── */

  reelOpened(reel, index) {
    /* ViewContent: merchant sees which products get reel views */
    this._fbq('track', 'ViewContent', {
      ...this._metaContent(reel),
      value:        this._parsePrice(reel?.productPrice),
      content_category: 'shoppable_reel',
    });
  },

  reelClosed(reel, index) {
    /* Custom — useful for drop-off analysis */
    this._fbq('trackCustom', 'ReelClosed', {
      ...this._metaContent(reel),
      reel_index: index,
    });
  },

  reelNavigated(reel, index, dir) {
    /* Custom — swipe/navigation behaviour */
    this._fbq('trackCustom', 'ReelNavigated', {
      ...this._metaContent(reel),
      direction:  dir,
      reel_index: index,
    });
  },

  videoPlayed(reel, index) {
    /* Custom — video engagement signal */
    this._fbq('trackCustom', 'VideoPlayed', {
      ...this._metaContent(reel),
      reel_index: index,
    });
  },

  videoEnded(reel, index) {
    /* Custom — 100% video completion, strong intent signal */
    this._fbq('trackCustom', 'VideoCompleted', {
      ...this._metaContent(reel),
      reel_index: index,
    });
  },

  liked(reel, index, isLiked) {
    if (!isLiked) return; /* only track new likes, not unlikes */
    this._fbq('trackCustom', 'ReelLiked', {
      ...this._metaContent(reel),
      reel_index: index,
    });
  },

  shared(reel, index, channel) {
    /* Standard Share event + channel as custom param */
    this._fbq('track', 'CustomizeProduct', {   /* closest standard to sharing intent */
      ...this._metaContent(reel),
      value:   this._parsePrice(reel?.productPrice),
    });
    this._fbq('trackCustom', 'ReelShared', {
      ...this._metaContent(reel),
      channel,
      reel_index: index,
    });
  },

  panelExpanded(reel, index) {
    /* AddPaymentInfo-level intent: user opened the buy panel */
    this._fbq('trackCustom', 'ReelShopNowOpened', {
      ...this._metaContent(reel),
      value:      this._parsePrice(reel?.productPrice),
      reel_index: index,
    });
  },

  panelCollapsed(reel, index) { /* no Meta event needed */ },

  addToCart(reel, index, variantId, qty) {
    /* Standard AddToCart — feeds directly into Meta CAPI & retargeting */
    this._fbq('track', 'AddToCart', {
      ...this._metaContent(reel),
      value:      this._parsePrice(reel?.productPrice) * qty,
      num_items:  qty,
    });
  },

  moreInfo(reel, index) {
    /* InitiateCheckout is too strong; use custom high-intent event */
    this._fbq('trackCustom', 'ReelMoreInfoClicked', {
      ...this._metaContent(reel),
      value:      this._parsePrice(reel?.productPrice),
      reel_index: index,
    });
  },

  carouselCardClicked(reel, index) {
    /* Custom — measures which thumbnail positions get most taps */
    this._fbq('trackCustom', 'ReelCardClicked', {
      ...this._metaContent(reel),
      reel_index: index,
    });
  },
};

/* ============================================================
   1. SECTION CAROUSEL — autoplay + snap scroll nav
   ============================================================ */

class ReelVideoObserver {
  constructor(video, options = {}) {
    this.video   = video;
    this.autoplay = options.autoplay !== false;
    this.loop     = options.loop    !== false;
    this.loaded   = false;

    if (this.loop) video.setAttribute('loop', '');
    video.muted     = true;
    video.playsInline = true;

    this._obs = new IntersectionObserver(this._onIntersect.bind(this), { threshold: 0.5 });
    this._obs.observe(video);
  }

  _onIntersect(entries) {
    if (!this.autoplay) return;
    if (entries[0].isIntersecting) {
      this._play();
    } else {
      this.video.pause();
    }
  }

  _play() {
    if (!this.loaded) {
      this.video.querySelectorAll('source[data-src]').forEach((s) => {
        s.src = s.dataset.src;
        delete s.dataset.src;
      });
      if (this.video.dataset.src) {
        this.video.src = this.video.dataset.src;
        delete this.video.dataset.src;
      }
      this.video.load();
      this.loaded = true;
    }
    this.video.play().catch(() => {});
  }
}

class ReelCarouselNav {
  constructor(wrapper) {
    this.track   = wrapper.querySelector('.shoppable-reels__track');
    this.prevBtn = wrapper.querySelector('.shoppable-reels__nav--prev');
    this.nextBtn = wrapper.querySelector('.shoppable-reels__nav--next');
    if (!this.track) return;

    const origItems = Array.from(this.track.querySelectorAll('.shoppable-reel-item'));
    this._n = origItems.length;
    if (this._n === 0) return;

    // Prepend clones → last card physically sits LEFT of first card
    const preFrag = document.createDocumentFragment();
    origItems.forEach(item => preFrag.appendChild(item.cloneNode(true)));
    this.track.insertBefore(preFrag, this.track.firstChild);

    // Append clones → first card physically sits RIGHT of last card
    origItems.forEach(item => this.track.appendChild(item.cloneNode(true)));

    // DOM is now: [pre-clones 0..n-1] [originals 0..n-1] [post-clones 0..n-1]
    // Absolute index of first original = n
    this._abs = this._n;
    this._busy = false;

    this.prevBtn?.addEventListener('click', () => this._scroll(-1));
    this.nextBtn?.addEventListener('click', () => this._scroll(1));
    if (this.prevBtn) this.prevBtn.disabled = false;
    if (this.nextBtn) this.nextBtn.disabled = false;

    // Init position after layout
    requestAnimationFrame(() => this._jumpToAbs(this._abs));
  }

  _perItem() {
    const item = this.track.querySelector('.shoppable-reel-item');
    if (!item) return 0;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 12;
    return item.offsetWidth + gap;
  }

  _jumpToAbs(absIdx) {
    this.track.style.scrollBehavior = 'auto';
    this.track.scrollLeft = absIdx * this._perItem();
    this.track.style.scrollBehavior = '';
    this._abs = absIdx;
  }

  _scroll(dir) {
    if (this._busy) return;
    this._busy = true;

    const step = 2; // items per click
    const n    = this._n;
    const pi   = this._perItem();
    const newAbs = this._abs + dir * step;

    // Smooth scroll into whichever clone zone is needed
    this.track.scrollTo({ left: newAbs * pi, behavior: 'smooth' });

    setTimeout(() => {
      if (newAbs >= 2 * n) {
        // Scrolled into post-clone zone → silently jump back to originals
        this._jumpToAbs(newAbs - n);
      } else if (newAbs < n) {
        // Scrolled into pre-clone zone → silently jump forward to originals
        this._jumpToAbs(newAbs + n);
      } else {
        this._abs = newAbs;
      }
      this._busy = false;
    }, 430);
  }
}

/* ============================================================
   2. REEL VIEWER MODAL
   ============================================================ */

class ShoppableReelsViewer {
  constructor(modal, reels) {
    this.modal    = modal;
    this.reels    = reels;          // array of reel data objects
    this.index    = 0;
    this.muted    = true;
    this.quantity = 1;
    this._cache   = {};             // product JSON cache

    /* DOM refs (populated in _buildShell) */
    this._activeReel  = null;
    this._prevReel    = null;
    this._nextReel    = null;
    this._videoEl     = null;
    this._panel       = null;
    this._dots        = null;

    this._buildShell();
    this._bindGlobal();
  }

  /* ── Public API ── */

  open(index, expandPanel = false) {
    this.index    = Math.max(0, Math.min(index, this.reels.length - 1));
    this.quantity = 1;
    this._render();
    this.modal.classList.add('is-open');
    document.body.classList.add('overflow-hidden');
    if (typeof trapFocus === 'function') trapFocus(this.modal);
    if (expandPanel) this._expandPanel();
    SRAnalytics.reelOpened(this.reels[this.index], this.index);
  }

  close() {
    SRAnalytics.reelClosed(this.reels[this.index], this.index);
    this._stopVideo();
    this._activeReel?.querySelector('.sr-panel-backdrop')?.remove();
    this.modal.classList.remove('is-open');
    document.body.classList.remove('overflow-hidden');
    if (typeof removeTrapFocus === 'function') removeTrapFocus();
  }

  navigate(delta) {
    const next = this.index + delta;
    if (next < 0 || next >= this.reels.length) return;
    this._stopVideo();
    this._closeSharePopup();
    this.index    = next;
    this.quantity = 1;
    this._collapsePanel();
    this._render();
    SRAnalytics.reelNavigated(this.reels[this.index], this.index, delta > 0 ? 'next' : 'prev');

    /* Slide animation */
    const animClass = delta > 0 ? 'sr-anim-next' : 'sr-anim-prev';
    this._activeReel.classList.remove('sr-anim-next', 'sr-anim-prev');
    requestAnimationFrame(() => {
      this._activeReel.classList.add(animClass);
      this._activeReel.addEventListener('animationend', () => {
        this._activeReel.classList.remove(animClass);
      }, { once: true });
    });
  }

  /* ── Build the viewer shell (runs once) ── */

  _buildShell() {
    this.modal.innerHTML = `
      <button class="sr-viewer__close" aria-label="Close viewer" type="button">
        <svg viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.9" stroke-linecap="round"/>
        </svg>
      </button>

      <div class="sr-viewer__track">
        <!-- prev reel -->
        <div class="sr-viewer__reel sr-viewer__reel--prev" role="button" aria-label="Previous reel" tabindex="0">
          <img class="sr-viewer__reel-thumb" alt="" loading="lazy">
        </div>

        <!-- active reel -->
        <div class="sr-viewer__reel sr-viewer__reel--active">
          <img class="sr-viewer__reel-thumb" alt="">
          <video class="sr-viewer__video" muted playsinline preload="none"></video>

          <button class="sr-viewer__sound" aria-label="Toggle sound" type="button">
            ${this._iconMuted()}
          </button>

          <div class="sr-viewer__side-actions">
            <button class="sr-viewer__action-btn sr-viewer__like-btn" type="button" aria-label="Like">
              <span class="sr-viewer__action-btn__icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
                    stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <span class="sr-action-count"></span>
              <span>Like</span>
            </button>
            <button class="sr-viewer__action-btn sr-viewer__share-btn" type="button" aria-label="Share">
              <span class="sr-viewer__action-btn__icon">
                <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="18" cy="5" r="3" stroke="white" stroke-width="1.6"/>
                  <circle cx="6" cy="12" r="3" stroke="white" stroke-width="1.6"/>
                  <circle cx="18" cy="19" r="3" stroke="white" stroke-width="1.6"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" stroke="white" stroke-width="1.6"/>
                  <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" stroke="white" stroke-width="1.6"/>
                </svg>
              </span>
              <span>Share</span>
            </button>
          </div>

          <!-- Product panel (compact + expanded inside) -->
          <div class="sr-viewer__product-panel">
            <div class="sr-panel-compact"></div>
            <div class="sr-panel-expanded"></div>
          </div>
        </div>

        <!-- next reel -->
        <div class="sr-viewer__reel sr-viewer__reel--next" role="button" aria-label="Next reel" tabindex="0">
          <img class="sr-viewer__reel-thumb" alt="" loading="lazy">
        </div>
      </div>

      <!-- Navigation arrows -->
      <button class="sr-viewer__nav sr-viewer__nav--prev" type="button" aria-label="Previous reel">
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 13L5 8l5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      <button class="sr-viewer__nav sr-viewer__nav--next" type="button" aria-label="Next reel">
        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6 3l5 5-5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>

      <!-- Indicator dots -->
      <div class="sr-viewer__dots"></div>`;

    /* Cache DOM refs */
    this._activeReel = this.modal.querySelector('.sr-viewer__reel--active');
    this._prevReel   = this.modal.querySelector('.sr-viewer__reel--prev');
    this._nextReel   = this.modal.querySelector('.sr-viewer__reel--next');
    this._videoEl    = this.modal.querySelector('.sr-viewer__video');
    this._panel      = this.modal.querySelector('.sr-viewer__product-panel');
    this._dots       = this.modal.querySelector('.sr-viewer__dots');

    /* Expose reel width as CSS var for nav positioning */
    this._setReelWidthVar();

    /* Build dots */
    this._buildDots();
  }

  /* ── Render the current index ── */

  _render() {
    const reel     = this.reels[this.index];
    const prevReel = this.reels[this.index - 1];
    const nextReel = this.reels[this.index + 1];

    /* Active video + thumb */
    const activeThumb = this._activeReel.querySelector('.sr-viewer__reel-thumb');
    if (reel.thumbSrc) { activeThumb.src = reel.thumbSrc; activeThumb.alt = reel.reelTitle; }
    activeThumb.style.opacity = '1';
    this._activeReel.classList.remove('is-playing');

    /* Load & play video */
    this._playVideo(reel.videoSources);

    /* Side reels */
    this._renderSide(this._prevReel, prevReel);
    this._renderSide(this._nextReel, nextReel);

    /* Nav arrow visibility */
    const prevNav = this.modal.querySelector('.sr-viewer__nav--prev');
    const nextNav = this.modal.querySelector('.sr-viewer__nav--next');
    if (prevNav) prevNav.disabled = !prevReel;
    if (nextNav) nextNav.disabled = !nextReel;

    /* Dots */
    this._updateDots();

    /* Like button state */
    this._updateLikeBtn();

    /* Product panel — compact state */
    this._collapsePanel();
    this._renderCompact(reel);
  }

  _renderSide(el, reel) {
    if (!reel) {
      el.style.visibility = 'hidden';
      el.setAttribute('aria-hidden', 'true');
      return;
    }
    el.style.visibility = '';
    el.removeAttribute('aria-hidden');
    const img = el.querySelector('.sr-viewer__reel-thumb');
    img.src = reel.thumbSrc || '';
    img.alt = reel.reelTitle || '';
  }

  /* ── Video ── */

  _playVideo(sources) {
    const v = this._videoEl;
    v.pause();
    /* Remove old sources */
    while (v.firstChild) v.removeChild(v.firstChild);

    /* Remove previous ended listener */
    if (this._onVideoEnded) {
      v.removeEventListener('ended', this._onVideoEnded);
    }

    if (!sources || !sources.length) return;

    sources.forEach(({ url, type }) => {
      const s = document.createElement('source');
      s.src  = url;
      if (type) s.type = type;
      v.appendChild(s);
    });

    v.muted = this.muted;
    v.loop  = false;
    v.load();
    v.play().then(() => {
      this._activeReel.classList.add('is-playing');
      SRAnalytics.videoPlayed(this.reels[this.index], this.index);
    }).catch(() => {});

    /* Autoplay next reel when this one ends */
    this._onVideoEnded = () => {
      SRAnalytics.videoEnded(this.reels[this.index], this.index);
      if (this.index < this.reels.length - 1) {
        this.navigate(1);
      }
    };
    v.addEventListener('ended', this._onVideoEnded);
  }

  _stopVideo() {
    this._videoEl?.pause();
    this._activeReel?.classList.remove('is-playing');
  }

  /* ── Sound toggle ── */

  _toggleSound() {
    this.muted = !this.muted;
    this._videoEl.muted = this.muted;
    const btn = this.modal.querySelector('.sr-viewer__sound');
    if (btn) btn.innerHTML = this.muted ? this._iconMuted() : this._iconUnmuted();
  }

  _iconMuted() {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <line x1="23" y1="9" x2="17" y2="15" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
      <line x1="17" y1="9" x2="23" y2="15" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
    </svg>`;
  }

  _iconUnmuted() {
    return `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M11 5L6 9H2v6h4l5 4V5z" stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M19.07 4.93a10 10 0 010 14.14M15.54 8.46a5 5 0 010 7.07" stroke="white" stroke-width="1.6" stroke-linecap="round"/>
    </svg>`;
  }

  /* ── Compact product panel ── */

  _renderCompact(reel) {
    const el = this._panel.querySelector('.sr-panel-compact');
    if (!reel.productHandle) {
      el.innerHTML = '';
      this._panel.style.display = 'none';
      return;
    }
    this._panel.style.display = '';

    const hasDiscount = reel.productComparePrice && reel.productComparePrice !== reel.productPrice;
    const compareCents = this._parseMoney(reel.productComparePrice);
    const saleCents    = this._parseMoney(reel.productPrice);
    const pct = hasDiscount && compareCents > 0
      ? Math.round((1 - saleCents / compareCents) * 100)
      : null;

    el.innerHTML = `
      <div class="sr-panel-compact__row">
        ${reel.productImage
          ? `<div class="sr-panel-compact__thumb">
               <img src="${reel.productImage}" alt="${this._esc(reel.productTitle)}" loading="lazy">
             </div>`
          : ''}
        <div class="sr-panel-compact__info">
          <p class="sr-panel-compact__name">${this._esc(reel.productTitle)}</p>
          <div class="sr-panel-compact__price-row">
            <span class="sr-panel-compact__sale">${reel.productPrice}</span>
            ${hasDiscount ? `<s class="sr-panel-compact__compare">${reel.productComparePrice}</s>` : ''}
            ${pct ? `<span class="sr-panel-compact__badge">${pct}% OFF</span>` : ''}
          </div>
        </div>
      </div>
      <button class="sr-panel-compact__atc" type="button">ADD TO CART</button>`;

    /* Update CSS var so side-actions stay above the panel */
    requestAnimationFrame(() => {
      const h = this._panel.offsetHeight;
      if (h) this._activeReel.style.setProperty('--sr-panel-h', h + 'px');
    });

    /* Tapping the row area expands to full shop view */
    el.querySelector('.sr-panel-compact__row')
      .addEventListener('click', () => this._expandPanel());

    /* ATC on compact: if product has options → expand for variant selection.
       If single variant → add directly. */
    el.querySelector('.sr-panel-compact__atc')
      .addEventListener('click', (e) => {
        e.stopPropagation();
        this._expandPanel();
      });
  }

  /* ── Expanded product panel ── */

  _expandPanel() {
    if (!this.reels[this.index].productHandle) return;
    SRAnalytics.panelExpanded(this.reels[this.index], this.index);

    /* Add a backdrop that collapses the panel on outside tap */
    if (!this._activeReel.querySelector('.sr-panel-backdrop')) {
      const bd = document.createElement('div');
      bd.className = 'sr-panel-backdrop';
      bd.addEventListener('click', () => this._collapsePanel());
      this._activeReel.insertBefore(bd, this._panel);
    }

    this._panel.classList.add('is-expanded');
    this._renderExpanded(this.reels[this.index]);
  }

  _collapsePanel() {
    if (this._panel.classList.contains('is-expanded')) {
      SRAnalytics.panelCollapsed(this.reels[this.index], this.index);
    }
    this._panel.classList.remove('is-expanded');
    this._panel.querySelector('.sr-panel-expanded').innerHTML = '';
    this._activeReel.querySelector('.sr-panel-backdrop')?.remove();
  }

  async _renderExpanded(reel) {
    const el = this._panel.querySelector('.sr-panel-expanded');
    el.innerHTML = this._expandedLoading();

    try {
      const product = await this._fetchProduct(reel.productHandle);
      this._buildExpandedHTML(el, product, reel);
    } catch {
      el.innerHTML = `<p style="padding:2rem;text-align:center;color:#888;font-size:1.3rem">
        Could not load product. <a href="${this._esc(reel.productUrl)}" style="color:#6b0f2b">View page</a>
      </p>`;
    }
  }

  _expandedLoading() {
    return `
      <div class="sr-panel-expanded__image" style="display:flex;align-items:center;justify-content:center;">
        <button class="sr-panel-expanded__close" type="button" aria-label="Close">
          <svg viewBox="0 0 18 18" fill="none"><path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
        <svg aria-hidden="true" width="28" height="28" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
          <circle fill="none" stroke="#6b0f2b" stroke-width="6" cx="33" cy="33" r="30"
            stroke-dasharray="80" stroke-dashoffset="60"
            style="transform-origin:center;animation:sr-spin 0.8s linear infinite"/>
        </svg>
      </div>`;
  }

  _buildExpandedHTML(el, product, reel) {
    const firstAvail = product.variants.find((v) => v.available) || product.variants[0];
    this._currentVariantId = firstAvail?.id;
    this._quantity = 1;

    const hasOptions = !(product.options.length === 1 && product.options[0] === 'Title');
    const isSoldOut  = !firstAvail?.available;
    const cartCount  = this._getCartCount();

    el.innerHTML = `
      <div class="sr-panel-expanded__image">
        <button class="sr-panel-expanded__close" type="button" aria-label="Close">
          <svg viewBox="0 0 18 18" fill="none"><path d="M1 1L17 17M17 1L1 17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>
        </button>
        ${reel.productImage
          ? `<img src="${reel.productImage}" alt="${this._esc(product.title)}" loading="lazy">`
          : ''}
      </div>

      <div class="sr-panel-expanded__body">
        <div class="sr-panel-expanded__name-row">
          <h3 class="sr-panel-expanded__name">${this._esc(product.title)}</h3>
          <a class="sr-panel-expanded__ext-link" href="${this._esc(reel.productUrl)}" target="_blank" rel="noopener" aria-label="View product page">
            <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M6 2H2v12h12v-4M9 2h5v5M14 2L8 8" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </a>
        </div>
        <div class="sr-panel-expanded__price-row">
          ${this._priceHTML(firstAvail)}
        </div>
      </div>

      ${hasOptions ? `<div class="sr-exp-variants">${this._buildOptions(product)}</div>` : ''}

      <div class="sr-exp-qty-row">
        <span class="sr-exp-qty-label">Quantity</span>
        <div class="sr-exp-qty-wrap">
          <button class="sr-exp-qty-btn" data-action="decrease" type="button" aria-label="Decrease">&#8722;</button>
          <input class="sr-exp-qty-value" type="number" value="1" min="1" max="99" aria-label="Quantity" readonly>
          <button class="sr-exp-qty-btn" data-action="increase" type="button" aria-label="Increase">&#43;</button>
        </div>
      </div>

      <div class="sr-exp-feedback"></div>
      ${isSoldOut ? '<p class="sr-exp-soldout">This item is currently unavailable.</p>' : ''}

      <div class="sr-exp-actions">
        <button class="sr-exp-atc-btn" type="button" ${isSoldOut ? 'disabled' : ''}>
          ${isSoldOut ? 'Sold Out' : 'Add to Cart'}
        </button>
        <a class="sr-exp-moreinfo-btn" href="${this._esc(reel.productUrl)}">More Info&nbsp;&#8594;</a>
        <a class="sr-exp-cart-btn" href="/cart" aria-label="View cart">
          <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
            <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
            <path d="M16 10a4 4 0 01-8 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          ${cartCount > 0 ? `<span class="sr-exp-cart-count">${cartCount}</span>` : ''}
        </a>
      </div>`;

    this._bindExpandedEvents(el, product, reel);
  }

  _bindExpandedEvents(el, product, reel) {
    el.querySelector('.sr-panel-expanded__close')
      ?.addEventListener('click', () => this._collapsePanel());

    el.querySelectorAll('.sr-exp-option-value').forEach((btn) =>
      btn.addEventListener('click', () => this._onOptionClick(btn, product, el))
    );

    el.querySelectorAll('.sr-exp-qty-btn').forEach((btn) =>
      btn.addEventListener('click', () => this._onQtyClick(btn, el))
    );

    el.querySelector('.sr-exp-atc-btn')
      ?.addEventListener('click', () => this._addToCart(reel, el));

    el.querySelector('.sr-exp-moreinfo-btn')
      ?.addEventListener('click', () => SRAnalytics.moreInfo(reel, this.index));
  }

  _onOptionClick(btn, product, el) {
    if (btn.classList.contains('is-unavailable')) return;

    const optIndex = parseInt(btn.dataset.optionIndex, 10);
    const newValue = btn.dataset.optionValue;

    /* Collect current selections */
    const selected = product.options.map((_, i) =>
      el.querySelector(`.sr-exp-option-value[data-option-index="${i}"].is-selected`)
        ?.dataset.optionValue || ''
    );
    selected[optIndex] = newValue;

    /* Update pill states */
    el.querySelectorAll(`.sr-exp-option-value[data-option-index="${optIndex}"]`)
      .forEach((b) => {
        const on = b.dataset.optionValue === newValue;
        b.classList.toggle('is-selected', on);
        b.setAttribute('aria-pressed', on);
      });

    /* Find matching variant */
    const match = product.variants.find((v) =>
      product.options.every((_, i) => v[`option${i + 1}`] === selected[i])
    );

    if (match) {
      this._currentVariantId = match.id;
      const priceRow = el.querySelector('.sr-panel-expanded__price-row');
      if (priceRow) priceRow.innerHTML = this._priceHTML(match);

      const atcBtn    = el.querySelector('.sr-exp-atc-btn');
      const soldNote  = el.querySelector('.sr-exp-soldout');
      if (atcBtn) {
        atcBtn.disabled    = !match.available;
        atcBtn.textContent = match.available ? 'Add to Cart' : 'Sold Out';
      }
      if (soldNote) soldNote.style.display = match.available ? 'none' : '';
    }
  }

  _onQtyClick(btn, el) {
    const input  = el.querySelector('.sr-exp-qty-value');
    const action = btn.dataset.action;
    let val = parseInt(input.value, 10) || 1;
    val = action === 'increase' ? Math.min(val + 1, 99) : Math.max(val - 1, 1);
    input.value   = val;
    this.quantity = val;
  }

  async _addToCart(reel, el) {
    const productUrl = reel?.productUrl || '';
    if (!this._currentVariantId) return;

    const atcBtn    = el.querySelector('.sr-exp-atc-btn');
    const feedback  = el.querySelector('.sr-exp-feedback');
    if (atcBtn)   { atcBtn.disabled = true; atcBtn.classList.add('is-loading'); }
    if (feedback)   feedback.className = 'sr-exp-feedback';

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({
          id: this._currentVariantId,
          quantity: this.quantity,
          sections: this._getCartSections(),
          sections_url: window.location.pathname,
        }),
      });

      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart.');

      SRAnalytics.addToCart(reel, this.index, this._currentVariantId, this.quantity);
      this._notifyCart(data);

      /* Update cart count badge */
      if (data.item_count !== undefined) {
        let badge = el.querySelector('.sr-exp-cart-count');
        const cartBtn = el.querySelector('.sr-exp-cart-btn');
        if (cartBtn) {
          if (!badge) { badge = document.createElement('span'); badge.className = 'sr-exp-cart-count'; cartBtn.appendChild(badge); }
          badge.textContent = data.item_count;
        }
      }

      if (feedback) {
        feedback.className = 'sr-exp-feedback is-success';
        feedback.textContent = '✓ Added to cart!';
        setTimeout(() => this.close(), 1500);
      } else {
        this.close();
      }
    } catch (err) {
      if (feedback) {
        feedback.className = 'sr-exp-feedback is-error';
        feedback.textContent = err.message || 'Something went wrong. Please try again.';
      }
    } finally {
      if (atcBtn) { atcBtn.disabled = false; atcBtn.classList.remove('is-loading'); }
    }
  }

  /* ── Global event binding ── */

  _bindGlobal() {
    /* Close button */
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.sr-viewer__close')) this.close();
    });

    /* Overlay click (outside the track) closes viewer */
    this.modal.addEventListener('click', (e) => {
      if (e.target === this.modal) this.close();
    });

    /* Escape key */
    document.addEventListener('keydown', (e) => {
      if (!this.modal.classList.contains('is-open')) return;
      if (e.key === 'Escape') { e.preventDefault(); this.close(); }
      if (e.key === 'ArrowLeft')  this.navigate(-1);
      if (e.key === 'ArrowRight') this.navigate(1);
    });

    /* Sound toggle */
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.sr-viewer__sound')) this._toggleSound();
    });

    /* Nav arrows */
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.sr-viewer__nav--prev')) this.navigate(-1);
      if (e.target.closest('.sr-viewer__nav--next')) this.navigate(1);
    });

    /* Side reels click */
    this._prevReel?.addEventListener('click', () => this.navigate(-1));
    this._nextReel?.addEventListener('click', () => this.navigate(1));

    /* Keyboard on side reels */
    [this._prevReel, this._nextReel].forEach((el, i) => {
      el?.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.navigate(i === 0 ? -1 : 1); }
      });
    });

    /* Like button */
    this.modal.addEventListener('click', (e) => {
      const btn = e.target.closest('.sr-viewer__like-btn');
      if (!btn) return;
      const reel = this.reels[this.index];
      const key  = `sr-like-${reel.productHandle || this.index}`;
      const liked = localStorage.getItem(key) === '1';
      if (liked) {
        localStorage.removeItem(key);
        this._setLikeCount(key, -1);
      } else {
        localStorage.setItem(key, '1');
        this._setLikeCount(key, 1);
      }
      this._updateLikeBtn();
      SRAnalytics.liked(reel, this.index, !liked);
    });

    /* Share button */
    this.modal.addEventListener('click', (e) => {
      if (!e.target.closest('.sr-viewer__share-btn')) return;
      this._openSharePopup();
    });

    /* Close share popup */
    this.modal.addEventListener('click', (e) => {
      if (e.target.closest('.sr-share-popup__close') || e.target.classList.contains('sr-share-popup')) {
        this._closeSharePopup();
      }
    });

    /* Collapse product panel when tapping the video area (outside the panel) */
    this._activeReel.addEventListener('click', (e) => {
      if (this._panel.classList.contains('is-expanded') && !e.target.closest('.sr-viewer__product-panel')) {
        this._collapsePanel();
      }
    });

    /* Touch swipe on active reel */
    let touchStartX = 0;
    this._activeReel?.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this._activeReel?.addEventListener('touchend', (e) => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 50) this.navigate(diff > 0 ? 1 : -1);
    }, { passive: true });
  }

  /* ── Like helpers ── */

  _likeKey(reel) {
    return `sr-like-${reel.productHandle || this.index}`;
  }

  _getLikeCount(key) {
    return parseInt(localStorage.getItem(`${key}-count`) || '0', 10);
  }

  _setLikeCount(key, delta) {
    const cur = this._getLikeCount(key);
    const next = Math.max(0, cur + delta);
    localStorage.setItem(`${key}-count`, String(next));
  }

  _updateLikeBtn() {
    const reel  = this.reels[this.index];
    const key   = this._likeKey(reel);
    const liked = localStorage.getItem(key) === '1';
    const count = this._getLikeCount(key);
    const btn   = this.modal.querySelector('.sr-viewer__like-btn');
    if (!btn) return;

    btn.classList.toggle('is-liked', liked);
    btn.querySelector('.sr-action-count')
      ? (btn.querySelector('.sr-action-count').textContent = count > 0 ? count : '')
      : null;
    const iconEl = btn.querySelector('.sr-viewer__action-btn__icon');
    if (iconEl) {
      iconEl.innerHTML = liked
        ? `<svg viewBox="0 0 24 24" fill="#e05" xmlns="http://www.w3.org/2000/svg">
             <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
               fill="#e05" stroke="#e05" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`
        : `<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"
               stroke="white" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
           </svg>`;
    }
  }

  /* ── Share popup ── */

  _openSharePopup() {
    const reel      = this.reels[this.index];
    const productUrl = reel.productUrl || '';
    const rawUrl    = productUrl
      ? (productUrl.startsWith('http') ? productUrl : `https://anekastudio.in${productUrl}`)
      : window.location.href;
    const shareMsg = encodeURIComponent(
      `Hey! Checkout the video of this amazing product I discovered! ${rawUrl}`
    );

    this._closeSharePopup();

    const popup = document.createElement('div');
    popup.className = 'sr-share-popup';
    popup.setAttribute('role', 'dialog');
    popup.setAttribute('aria-modal', 'true');
    popup.setAttribute('aria-label', 'Share');
    popup.innerHTML = `
      <div class="sr-share-popup__card">
        <div class="sr-share-popup__header">
          <button class="sr-share-popup__close" type="button" aria-label="Close share">&#x2715;</button>
        </div>
        <div class="sr-share-popup__divider"></div>
        <div class="sr-share-popup__grid">

          <a class="sr-share-option" href="https://wa.me/?text=${shareMsg}" target="_blank" rel="noopener" data-channel="whatsapp">
            <span class="sr-share-option__icon">
              <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#25D366"/>
                <path d="M34.5 13.5C31.8 10.8 28.2 9.3 24.4 9.3c-7.9 0-14.3 6.4-14.3 14.3 0 2.5.7 5 1.9 7.2L10 39l8.4-2.2c2.1 1.1 4.5 1.7 6.9 1.7h.1c7.9 0 14.3-6.4 14.3-14.3-.1-3.8-1.5-7.4-4.2-10.2zM24.5 36c-2.2 0-4.3-.6-6.1-1.7l-.4-.3-4.4 1.2 1.2-4.3-.3-.4c-1.2-1.9-1.9-4.1-1.9-6.4 0-6.6 5.4-11.9 12-11.9 3.2 0 6.2 1.2 8.5 3.5 2.3 2.3 3.5 5.3 3.5 8.5-.1 6.5-5.4 11.8-12.1 11.8zm6.6-8.9c-.4-.2-2.2-1.1-2.5-1.2-.3-.1-.6-.2-.8.2-.2.4-.9 1.2-1.1 1.4-.2.2-.4.2-.8.1s-1.6-.6-3-1.9c-1.1-1-1.9-2.2-2.1-2.6-.2-.4 0-.6.2-.8l.5-.6c.2-.2.2-.4.4-.6.1-.2.1-.4 0-.6-.1-.2-.8-2-1.1-2.7-.3-.7-.6-.6-.8-.6h-.7c-.2 0-.6.1-.9.4-.3.3-1.2 1.2-1.2 2.9s1.2 3.3 1.4 3.6c.2.2 2.4 3.8 5.9 5.3.8.4 1.5.6 2 .7.8.3 1.6.2 2.2.1.7-.1 2.1-.9 2.4-1.7.3-.8.3-1.5.2-1.7-.1-.1-.3-.2-.7-.4z" fill="white"/>
              </svg>
            </span>
            <span>Whatsapp</span>
          </a>

          <a class="sr-share-option" href="https://twitter.com/intent/tweet?text=${shareMsg}" target="_blank" rel="noopener" data-channel="twitter">
            <span class="sr-share-option__icon">
              <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#000"/>
                <path d="M26.37 22.43L33.48 14h-1.69l-6.15 7.15L20.82 14H15l7.47 10.87L15 34h1.69l6.53-7.59L28.18 34H34l-7.63-11.57zm-2.31 2.69l-.76-1.08-6-8.58h2.59l4.85 6.94.75 1.08 6.3 9.01h-2.59l-5.14-7.37z" fill="white"/>
              </svg>
            </span>
            <span>X</span>
          </a>

          <button class="sr-share-option" type="button" data-channel="copy">
            <span class="sr-share-option__icon">
              <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#e2e2e2"/>
                <rect x="17" y="20" width="13" height="15" rx="2" stroke="#444" stroke-width="2" fill="none"/>
                <path d="M20 20v-3a2 2 0 012-2h9a2 2 0 012 2v11a2 2 0 01-2 2h-2" stroke="#444" stroke-width="2" fill="none" stroke-linecap="round"/>
              </svg>
            </span>
            <span class="sr-copy-label">Copy link</span>
          </button>

          <button class="sr-share-option" type="button" data-channel="more">
            <span class="sr-share-option__icon">
              <svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <circle cx="24" cy="24" r="24" fill="#e2e2e2"/>
                <circle cx="15" cy="24" r="2.5" fill="#555"/>
                <circle cx="24" cy="24" r="2.5" fill="#555"/>
                <circle cx="33" cy="24" r="2.5" fill="#555"/>
              </svg>
            </span>
            <span>More</span>
          </button>

        </div>
      </div>`;

    /* Track whatsapp / x clicks via link click */
    popup.querySelector('[data-channel="whatsapp"]').addEventListener('click', () => {
      SRAnalytics.shared(reel, this.index, 'whatsapp');
    });
    popup.querySelector('[data-channel="twitter"]').addEventListener('click', () => {
      SRAnalytics.shared(reel, this.index, 'x_twitter');
    });

    popup.querySelector('[data-channel="copy"]').addEventListener('click', () => {
      navigator.clipboard?.writeText(rawUrl).then(() => {
        SRAnalytics.shared(reel, this.index, 'copy_link');
        const label = popup.querySelector('.sr-copy-label');
        if (label) { label.textContent = 'Copied!'; setTimeout(() => { label.textContent = 'Copy link'; }, 2000); }
      }).catch(() => {});
    });

    popup.querySelector('[data-channel="more"]').addEventListener('click', () => {
      SRAnalytics.shared(reel, this.index, 'more');
      if (navigator.share) {
        navigator.share({ title: reel.reelTitle || reel.productTitle || '', url: rawUrl }).catch(() => {});
      } else {
        navigator.clipboard?.writeText(rawUrl).catch(() => {});
      }
    });

    this._activeReel.appendChild(popup);
  }

  _closeSharePopup() {
    this._activeReel?.querySelector('.sr-share-popup')?.remove();
  }

  /* ── Dots ── */

  _buildDots() {
    if (!this._dots) return;
    this._dots.innerHTML = this.reels
      .map((_, i) => `<div class="sr-viewer__dot${i === 0 ? ' is-active' : ''}" data-index="${i}"></div>`)
      .join('');

    this._dots.addEventListener('click', (e) => {
      const dot = e.target.closest('.sr-viewer__dot');
      if (dot) { this._stopVideo(); this.index = parseInt(dot.dataset.index, 10); this._collapsePanel(); this._render(); }
    });
  }

  _updateDots() {
    this._dots?.querySelectorAll('.sr-viewer__dot').forEach((d, i) => {
      d.classList.toggle('is-active', i === this.index);
    });
  }

  /* ── Product helpers ── */

  async _fetchProduct(handle) {
    if (this._cache[handle]) return this._cache[handle];
    const res = await fetch(`/products/${handle}.js`);
    if (!res.ok) throw new Error('Fetch failed');
    const data = await res.json();
    this._cache[handle] = data;
    return data;
  }

  _priceHTML(variant) {
    if (!variant) return '';
    const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
    const pct = hasDiscount ? Math.round((1 - variant.price / variant.compare_at_price) * 100) : null;
    return [
      hasDiscount ? `<s class="sr-exp-compare">${this._fmt(variant.compare_at_price)}</s>` : '',
      `<span class="sr-exp-price">${this._fmt(variant.price)}</span>`,
      pct ? `<span class="sr-exp-badge">${pct}% off</span>` : '',
    ].join('');
  }

  _buildOptions(product) {
    return product.options.map((opt, optIndex) => {
      /* Shopify returns options as strings; guard against object format */
      const optionName = typeof opt === 'string' ? opt : (opt?.name || `Option ${optIndex + 1}`);
      const values = [...new Set(product.variants.map((v) => v[`option${optIndex + 1}`]))];
      const selVal = product.variants.find((v) => v.id === this._currentVariantId)
        ?.[`option${optIndex + 1}`] || values[0];

      const pills = values.map((val) => {
        const avail = product.variants.some((v) => v[`option${optIndex + 1}`] === val && v.available);
        return `<button
          class="sr-exp-option-value${val === selVal ? ' is-selected' : ''}${!avail ? ' is-unavailable' : ''}"
          data-option-index="${optIndex}" data-option-value="${this._esc(val)}"
          type="button" aria-pressed="${val === selVal}" ${!avail ? 'aria-disabled="true"' : ''}
        >${this._esc(val)}</button>`;
      }).join('');

      return `<div class="sr-exp-option-group">
        <span class="sr-exp-option-label">${this._esc(optionName)}</span>
        <div class="sr-exp-option-values">${pills}</div>
      </div>`;
    }).join('');
  }

  _getCartSections() {
    for (const sel of ['cart-drawer', 'cart-notification']) {
      const el = document.querySelector(sel);
      if (el && typeof el.getSectionsToRender === 'function')
        return el.getSectionsToRender().map((s) => s.id);
    }
    return [];
  }

  _notifyCart(cartData) {
    const cartDrawer = document.querySelector('cart-drawer');
    const cartNote   = document.querySelector('cart-notification');
    const target     = cartDrawer || cartNote;

    if (target && typeof target.renderContents === 'function') {
      target.renderContents(cartData);
      if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
    } else if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'shoppable-reels', cartData });
    }

    document.querySelectorAll('.cart-count-bubble span:first-of-type, [data-cart-count]').forEach((el) => {
      if (cartData.item_count !== undefined) el.textContent = cartData.item_count;
    });
  }

  _getCartCount() {
    const el = document.querySelector('.cart-count-bubble span:first-of-type');
    return el ? parseInt(el.textContent, 10) || 0 : 0;
  }

  /* ── CSS var for nav positioning ── */
  _setReelWidthVar() {
    const update = () => {
      const reel = this.modal.querySelector('.sr-viewer__reel--active');
      if (reel) this.modal.style.setProperty('--sr-reel-w', reel.offsetWidth + 'px');
    };
    update();
    window.addEventListener('resize', update, { passive: true });
  }

  /* ── Utilities ── */

  _parseMoney(str) {
    if (!str) return 0;
    return parseFloat(String(str).replace(/[^0-9.]/g, '')) * 100 || 0;
  }

  _fmt(cents) {
    if (window.Shopify?.formatMoney) {
      const fmt = document.querySelector('[data-money-format]')?.dataset.moneyFormat
        || window.theme?.moneyFormat || '₹{{amount}}';
      return window.Shopify.formatMoney(cents, fmt);
    }
    return `₹${(cents / 100).toFixed(2)}`;
  }

  _esc(str) {
    return String(str || '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}

/* ============================================================
   3. SECTION INIT
   ============================================================ */

function initShoppableReelsSection(section) {
  const wrapper = section.querySelector('.shoppable-reels__track-wrapper');
  if (!wrapper) return;

  const track    = wrapper.querySelector('.shoppable-reels__track');
  const autoplay = wrapper.dataset.autoplay !== 'false';
  const loop     = wrapper.dataset.loop    !== 'false';

  /* Section carousel: autoplay visible videos */
  track?.querySelectorAll('.shoppable-reel-card__video').forEach((v) => {
    new ReelVideoObserver(v, { autoplay, loop });
  });

  /* Section carousel: prev/next navigation */
  new ReelCarouselNav(wrapper);

  /* ── Collect reel data from the DOM ── */
  const reelData = [];
  track?.querySelectorAll('.shoppable-reel-item').forEach((item) => {
    const card = item.querySelector('.shoppable-reel-card');
    if (!card) return;

    /* Video sources (stored as data-src for lazy loading in the section) */
    const videoSources = [];
    card.querySelectorAll('.shoppable-reel-card__video source').forEach((s) => {
      const url = s.dataset.src || s.src;
      if (url) videoSources.push({ url, type: s.type || '' });
    });

    reelData.push({
      thumbSrc:            card.querySelector('.shoppable-reel-card__thumbnail')?.src || '',
      videoSources,
      reelTitle:           card.querySelector('.shoppable-reel-card__title')?.textContent?.trim() || '',
      productHandle:       card.dataset.productHandle  || '',
      productUrl:          card.dataset.productUrl     || '',
      productTitle:        card.dataset.productTitle   || '',
      productImage:        card.dataset.productImage   || '',
      productPrice:        card.dataset.productPrice   || '',
      productComparePrice: card.dataset.productComparePrice || '',
    });
  });

  /* ── Viewer modal ── */
  const sectionId = section.closest('[id^="shopify-section-"]')?.id.replace('shopify-section-', '')
    || section.dataset.sectionId;
  const modal = document.getElementById(`ShoppableReelsViewer-${sectionId}`);
  if (!modal || !reelData.length) return;

  const viewer = new ShoppableReelsViewer(modal, reelData);

  /* Open viewer when any reel card is clicked */
  track?.querySelectorAll('.shoppable-reel-item').forEach((item, i) => {
    const card = item.querySelector('.shoppable-reel-card');

    card?.addEventListener('click', () => {
      SRAnalytics.carouselCardClicked(reelData[i], i);
      viewer.open(i);
    });

    /* Keyboard: Enter / Space */
    card?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); viewer.open(i); }
    });
  });
}

/* ── Boot ── */
document.querySelectorAll('.shoppable-reels-section').forEach(initShoppableReelsSection);

document.addEventListener('shopify:section:load', (e) => {
  const el = e.target.querySelector('.shoppable-reels-section');
  if (el) initShoppableReelsSection(el);
});